import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import apiService from '../services/ApiService';
import Button from './ui/Button';
import type { AdminFeaturedBuild } from '../services/api/sponsorship';
import type { HomeVehicleCard } from './home/homeTypes';
import { resolvePublicFileUrl } from '../utils/imageUrls';
import { AdminQueueState } from './ui/AdminQueueState';

export const AdminFeaturedBuilds: React.FC = () => {
  const [items, setItems] = useState<AdminFeaturedBuild[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<HomeVehicleCard[]>([]);
  const [searching, setSearching] = useState(false);
  const [blurb, setBlurb] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.adminListFeaturedBuilds();
      setItems(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load featured builds');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const runSearch = async () => {
    if (!search.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const found = await apiService.searchVehicles({
        search: search.trim(),
        is_public: true,
        limit: 12,
      });
      setResults((found || []) as HomeVehicleCard[]);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVehicleId) {
      setFormError('Select a public vehicle first.');
      return;
    }
    setSaving(true);
    setFormError(null);
    setFormSuccess(null);
    try {
      await apiService.adminCreateFeaturedBuild({
        vehicle_id: selectedVehicleId,
        blurb: blurb.trim() || undefined,
        starts_at: startsAt || null,
        ends_at: endsAt || null,
        is_active: true,
      });
      setFormSuccess('Featured build scheduled.');
      setBlurb('');
      setSelectedVehicleId(null);
      setStartsAt('');
      setEndsAt('');
      setResults([]);
      setSearch('');
      await load();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (id: number, is_active: boolean) => {
    try {
      await apiService.adminUpdateFeaturedBuild(id, {
        is_active,
        status: is_active ? 'active' : 'archived',
      });
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  };

  const archive = async (id: number) => {
    try {
      await apiService.adminArchiveFeaturedBuild(id);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Archive failed');
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <PageHeader eyebrow="Admin" title="Featured builds" description="Schedule editorial featured builds for Home." />
        <p className="text-gray-400 text-sm mt-1">
          Editorial placements for Home. Label is always “Featured build”, never Sponsored.
        </p>
      </div>

      {error ? (
        <AdminQueueState
          variant="error"
          title="Could not load featured builds"
          description={error}
          onRetry={() => void load()}
        />
      ) : null}

      <form onSubmit={create} className="space-y-4 border border-white/[0.08] rounded-xl p-4 sm:p-5 bg-[#141414]/40">
        <h2 className="text-white font-medium">Schedule featured build</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <label className="sr-only" htmlFor="featured-vehicle-search">
            Search public vehicles
          </label>
          <input
            id="featured-vehicle-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search public vehicles…"
            className="flex-1 bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
          />
          <Button type="button" variant="secondary" loading={searching} onClick={() => void runSearch()}>
            Search
          </Button>
        </div>
        {results.length > 0 ? (
          <ul className="max-h-56 overflow-y-auto divide-y divide-white/[0.06] border border-white/[0.08] rounded-lg">
            {results.map((v) => {
              const img = resolvePublicFileUrl(v.image_url);
              const selected = selectedVehicleId === v.id;
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedVehicleId(v.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/[0.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400 ${
                      selected ? 'bg-orange-950/40' : ''
                    }`}
                    aria-pressed={selected}
                  >
                    <div className="w-12 h-9 bg-[#0a0a0a] overflow-hidden rounded flex-shrink-0">
                      {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : null}
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm truncate">
                        {v.year} {v.make} {v.model}
                        {v.nickname ? ` · ${v.nickname}` : ''}
                      </p>
                      <p className="text-gray-500 text-xs truncate">@{v.owner_username}</p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
        <div>
          <label htmlFor="featured-blurb" className="block text-sm text-gray-400 mb-1">
            Curator blurb (optional)
          </label>
          <textarea
            id="featured-blurb"
            value={blurb}
            onChange={(e) => setBlurb(e.target.value)}
            rows={2}
            className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="featured-starts" className="block text-sm text-gray-400 mb-1">
              Starts
            </label>
            <input
              id="featured-starts"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
            />
          </div>
          <div>
            <label htmlFor="featured-ends" className="block text-sm text-gray-400 mb-1">
              Ends
            </label>
            <input
              id="featured-ends"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
            />
          </div>
        </div>
        {formError ? (
          <p role="alert" className="text-red-300 text-sm">
            {formError}
          </p>
        ) : null}
        {formSuccess ? (
          <p role="status" className="text-green-300 text-sm">
            {formSuccess}
          </p>
        ) : null}
        <Button type="submit" variant="primary" loading={saving}>
          Create & activate
        </Button>
      </form>

      <div>
        <h2 className="text-white font-medium mb-3">Current & scheduled</h2>
        {loading ? (
          <p className="text-gray-400 text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <AdminQueueState
            variant="empty"
            title="No featured builds scheduled"
            description="Schedule a public vehicle above to feature it on Home."
          />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
            <table className="min-w-full text-sm">
              <thead className="bg-[#141414]/80 text-gray-400 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Vehicle</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Window</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {items.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 text-white">
                      {row.vehicle
                        ? `${row.vehicle.year} ${row.vehicle.make} ${row.vehicle.model}`
                        : `#${row.vehicle_id}`}
                      {row.blurb ? <p className="text-gray-500 text-xs mt-0.5 line-clamp-1">{row.blurb}</p> : null}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {row.status || (row.is_active ? 'active' : 'inactive')}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {[row.starts_at, row.ends_at].filter(Boolean).join(' → ') || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {!row.is_active ? (
                          <Button type="button" variant="secondary" size="sm" onClick={() => void setActive(row.id, true)}>
                            Activate
                          </Button>
                        ) : (
                          <Button type="button" variant="ghost" size="sm" onClick={() => void setActive(row.id, false)}>
                            Pause
                          </Button>
                        )}
                        <Button type="button" variant="danger" size="sm" onClick={() => void archive(row.id)}>
                          Archive
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeaturedBuilds;
